import { AssetSheetColumnMapScope, AssetSheetLevel } from '@atlas/types'
import { z } from 'zod'

const nonBlank = (field: string) => z.string().trim().min(1, `${field} cannot be empty`)

/**
 * How a cell is read, rule by rule: a rule left out keeps what each target tab has, a null
 * removes it, so a change on every tab touches only the rules it names.
 */
const cellFormatSchema = z
	.object({
		required: z.boolean().nullable().optional(),
		// A separator is matched as written, so a space is a valid one; only the empty string is not.
		split: z.array(z.string().min(1, 'A separator cannot be empty')).nullable().optional(),
		ignore: z.array(nonBlank('An ignored part')).nullable().optional(),
		rootsOnly: z.boolean().nullable().optional(),
		parentColumn: nonBlank('The parent column').nullable().optional(),
	})
	.strict()

/**
 * PATCH /admin/asset-sheet/column-map/{id}. Every field is optional but one must be set; `scope`
 * defaults to the entry's own tab. Whether the value belongs to the axis, and whether a value or a
 * level fits the column, needs the registry and the entry, so the service checks those.
 */
export const assetSheetColumnMapUpdateSchema = z
	.object({
		columnName: nonBlank('The column name').max(200, 'The column name is longer than 200 characters').optional(),
		valueCode: nonBlank('The value').nullable().optional(),
		level: z.enum(AssetSheetLevel).nullable().optional(),
		cellFormat: cellFormatSchema.optional(),
		scope: z.enum(AssetSheetColumnMapScope).default(AssetSheetColumnMapScope.THIS_TAB),
	})
	.strict()
	.refine(({ scope: _scope, ...fields }) => Object.values(fields).some((v) => v !== undefined), {
		message: 'Set at least one of columnName, valueCode, level or cellFormat',
	})

export type AssetSheetColumnMapUpdateInput = z.infer<typeof assetSheetColumnMapUpdateSchema>
